import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Package, Mail, Phone, MapPin, ChevronDown, ChevronUp } from 'lucide-react';

const Orders = () => {
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState('processing');
  const [expandedOrder, setExpandedOrder] = useState(null);

  const statusColors = {
    processing: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Processing' },
    shipped: { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Shipped' },
    delivered: { bg: 'bg-green-100', text: 'text-green-700', label: 'Delivered' }
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/orders`);
      setOrders(response.data);
    } catch (error) {
      console.error('Error fetching orders:', error);
    }
  };

  const updateOrderStatus = async (id, status) => {
    try {
      await axios.put(`${process.env.REACT_APP_API_URL}/orders/${id}`, { orderStatus: status });
      fetchOrders();
    } catch (error) {
      console.error('Error updating order:', error);
    }
  };

  const OrderCard = ({ order }) => {
    const isExpanded = expandedOrder === order._id;
    const statusStyle = statusColors[order.orderStatus];
    
    return (
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
        <div className="p-3 sm:p-4">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 sm:gap-4">
            {/* Order Info */}
            <div className="flex items-start sm:items-center space-x-3">
              <Package className="text-gray-400 mt-1 sm:mt-0" size={20} />
              <div>
                <h3 className="font-medium">{order.name}</h3>
                <div className="flex flex-wrap items-center gap-2">
                  <p className="text-sm text-gray-500">
                    {new Date(order.createdAt).toLocaleDateString()}
                  </p>
                  <span className={`px-2 py-0.5 text-xs rounded-full ${statusStyle.bg} ${statusStyle.text} font-medium`}>
                    {statusStyle.label}
                  </span>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-between sm:justify-end space-x-3">
              <span className="text-lg font-medium">₦{order.amount.toLocaleString()}</span>
              <div className="flex items-center space-x-2">
                {order.orderStatus === 'processing' && (
                  <button
                    onClick={() => updateOrderStatus(order._id, 'shipped')}
                    className="px-3 py-1 text-sm bg-black text-white rounded hover:bg-gray-800"
                  >
                    Ship
                  </button>
                )}
                {order.orderStatus === 'shipped' && (
                  <button
                    onClick={() => updateOrderStatus(order._id, 'delivered')}
                    className="px-3 py-1 text-sm bg-black text-white rounded hover:bg-gray-800"
                  >
                    Deliver
                  </button>
                )}
                <button
                  onClick={() => setExpandedOrder(isExpanded ? null : order._id)}
                  className="text-gray-400 hover:text-gray-600 p-1"
                >
                  {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                </button>
              </div>
            </div>
          </div>
          
          {isExpanded && (
            <div className="mt-4 pt-4 border-t border-gray-100">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="flex items-start space-x-2">
                  <Mail className="text-gray-400 mt-1" size={16} />
                  <div>
                    <p className="text-sm text-gray-600">Email</p>
                    <p className="text-sm break-all">{order.email}</p>
                  </div>
                </div>
                <div className="flex items-start space-x-2">
                  <Phone className="text-gray-400 mt-1" size={16} />
                  <div>
                    <p className="text-sm text-gray-600">Phone</p>
                    <p className="text-sm">{order.phone}</p>
                  </div>
                </div>
                <div className="flex items-start space-x-2">
                  <MapPin className="text-gray-400 mt-1" size={16} />
                  <div>
                    <p className="text-sm text-gray-600">Address</p>
                    <p className="text-sm">{order.shippingAddress}</p>
                  </div>
                </div>
              </div>
              <div className="mt-4">
                <p className="text-sm text-gray-600 mb-2">Item(s) Ordered:</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {order.items.map((item, index) => (
                    <div key={index} className="text-sm">
                      {item.productName} × {item.productQuantity}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const filteredOrders = orders.filter(order => order.orderStatus === filter);

  return (
    <div className="p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <h2 className="text-2xl sm:text-3xl font-bold">Orders</h2>
        <div className="flex flex-wrap gap-2 w-full sm:w-auto">
          {['processing', 'shipped', 'delivered'].map((status) => (
            <button
              key={status}
              onClick={() => setFilter(status)}
              className={`flex-1 sm:flex-none px-3 py-1.5 text-sm rounded transition-colors ${
                filter === status
                  ? 'bg-black text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-3">
        {filteredOrders.map(order => (
          <OrderCard key={order._id} order={order} />
        ))}
        {filteredOrders.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            No {filter} orders found
          </div>
        )}
      </div>
    </div>
  );
};

export default Orders;